import { JSX, useMemo } from "react";

import { TrendingUp } from "@mui/icons-material";
import { Box, Typography, useTheme } from "@mui/material";
import { type Dayjs } from "dayjs";
import { t } from "i18next";
import { useHistoryChartQuery } from "src/api/locationChartQueries.api";
import IntervalType from "src/constants/intervalType.enum";
import MeasureType from "src/constants/measureType.enum";
import sharedStyles from "src/constants/styles";
import { formatNum, getCurrentTotalCount, getExpectedVisitors, getPastTotalCount } from "src/lib/number.helpers";
import { type MeasuresItemizedItemDto } from "src/types/locationChartDto";

import getStyles from "../helper/infoBar.helper";

interface ExpectedVisitorsComponentParams {
  chartData: MeasuresItemizedItemDto[];
  intervalType: IntervalType;
  locationId: string;
  date: Dayjs;
}

function ExpectedVisitorsComponent({ chartData, intervalType, locationId, date }: ExpectedVisitorsComponentParams): JSX.Element | null {
  const theme = useTheme();
  const { data: measuresWholeDayData } = useHistoryChartQuery(locationId, intervalType, date, MeasureType.Count);

  const chartWholeDayData = useMemo(() => measuresWholeDayData?.data ?? [], [measuresWholeDayData?.data]);
  const currentPeriodSum = useMemo(() => getCurrentTotalCount(chartData), [chartData]);
  const pastPeriodSum = useMemo(() => getPastTotalCount(chartData), [chartData]);
  const pastPeriodWholeDaySum = useMemo(() => getCurrentTotalCount(chartWholeDayData), [chartWholeDayData]);

  const expectedVisitors = useMemo(() => {
    if (chartData.length === 0) {
      return pastPeriodWholeDaySum;
    }
    return getExpectedVisitors(pastPeriodSum, currentPeriodSum, pastPeriodWholeDaySum);
  }, [chartData, pastPeriodSum, currentPeriodSum, pastPeriodWholeDaySum]);

  const showExpectedVisitorsRow = Boolean(measuresWholeDayData && chartWholeDayData.length > 0);

  if (!showExpectedVisitorsRow) {
    return null;
  }

  return (
    <Box sx={sharedStyles(theme).centerFlexbox} mt={2}>
      <Box sx={getStyles(theme).iconBox}>
        <TrendingUp sx={getStyles(theme).icon} fontSize='inherit' />
      </Box>
      <Typography color='primary' variant='h5'>
        {formatNum(Math.floor(expectedVisitors))}
      </Typography>
      {intervalType === IntervalType.Day && <Typography variant='body2'>{t("expected visitors today")}</Typography>}
      {intervalType === IntervalType.Month && <Typography variant='body2'>{t("expected visitors this month")}</Typography>}
      {intervalType === IntervalType.IsoYear && <Typography variant='body2'>{t("expected visitors this year")}</Typography>}
    </Box>
  );
}

export default ExpectedVisitorsComponent;
